import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { ArrowLeft, CalendarClock, Download, Loader2, Mail } from "lucide-react";

import ScheduleModal from "./ScheduleModal";
import { useDownloadReportPdf, useEmailReport } from "./api";

const buttonClass =
  "flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-200 disabled:opacity-60";
const selectClass =
  "rounded-md border border-navy-500 bg-navy-700 px-3 py-2 text-sm text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-200";

/**
 * The report's control bar: period, PDF, email, schedule. The PDF is rendered
 * by the backend — the same file the scheduled email attaches — so there is no
 * print stylesheet to keep in step.
 *
 * On iOS/Android a blob download goes nowhere inside the WebView, so native
 * builds offer "Enviar a mi correo" instead of a download button.
 */
const ReportActions = ({
  title,
  backTo,
  backLabel = "Volver",
  reportId,
  reportTitle,
  params,
  period,
  onPeriodChange,
  periods = [28, 14, 7],
  schedulable = true,
  allowGuardian = false,
  children,
}) => {
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const download = useDownloadReportPdf();
  const email = useEmailReport();
  const native = Capacitor.isNativePlatform();

  const status = download.error
    ? { tone: "error", text: download.error.message }
    : email.error
      ? { tone: "error", text: email.error.message }
      : email.isSuccess
        ? { tone: "ok", text: `Reporte enviado a ${email.data.recipients.join(", ")}.` }
        : null;

  return (
    <div className="z-20 bg-navy-900 text-white sm:sticky sm:top-0">
      <div className="flex flex-wrap items-center gap-2.5 px-4 py-3 sm:px-5">
        {backTo && (
          <Link
            to={backTo}
            className="flex items-center gap-1.5 rounded-md border border-navy-500 bg-navy-700 px-3 py-2 text-sm hover:bg-navy-500"
          >
            <ArrowLeft size={16} aria-hidden="true" />
            {backLabel}
          </Link>
        )}
        <h1 className="font-display text-[24px] leading-none tracking-wide">{title}</h1>

        <div className="flex-1" />

        {children}

        {onPeriodChange && (
          <label className="flex items-center gap-2 text-[12px] text-peri-200">
            Periodo
            <select value={period} onChange={(event) => onPeriodChange(Number(event.target.value))} className={selectClass}>
              {periods.map((days) => (
                <option key={days} value={days}>
                  {days} días
                </option>
              ))}
            </select>
          </label>
        )}

        {!native && (
          <button
            type="button"
            onClick={() => download.mutate({ reportId, params })}
            disabled={download.isPending}
            className={`${buttonClass} border-brand-400 bg-brand-400 text-white hover:bg-navy-500`}
          >
            {download.isPending ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Download size={16} aria-hidden="true" />}
            Descargar PDF
          </button>
        )}

        <button
          type="button"
          onClick={() => email.mutate({ reportId, params })}
          disabled={email.isPending}
          className={`${buttonClass} border-navy-500 bg-navy-700 text-white hover:bg-navy-500`}
        >
          {email.isPending ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Mail size={16} aria-hidden="true" />}
          Enviar a mi correo
        </button>

        {schedulable && (
          <button
            type="button"
            onClick={() => setScheduleOpen(true)}
            className={`${buttonClass} border-navy-500 bg-navy-700 text-white hover:bg-navy-500`}
          >
            <CalendarClock size={16} aria-hidden="true" />
            Programar envío
          </button>
        )}
      </div>

      {status && (
        <p
          role={status.tone === "error" ? "alert" : "status"}
          className={`px-4 pb-2.5 text-[13px] sm:px-5 ${status.tone === "error" ? "text-[#ffb4a3]" : "text-[#8fe3c5]"}`}
        >
          {status.text}
        </p>
      )}

      {schedulable && (
        <ScheduleModal
          isOpen={scheduleOpen}
          onClose={() => setScheduleOpen(false)}
          reportId={reportId}
          reportTitle={reportTitle}
          params={params}
          allowGuardian={allowGuardian}
        />
      )}
    </div>
  );
};

export default ReportActions;
